import type { DaemonTriggerResolvedSettings } from '@agentteams/runner-request/ui/lib/api';
export type {
  DaemonTriggerResolveSource,
  DaemonTriggerResolvedField,
  DaemonTriggerResolvedSettings,
} from '@agentteams/runner-request/ui/lib/api';

import { RUNNER_EFFORT_LEVELS, isEffortRunnerType } from '@agentteams/core-constants';
import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError, PaginationMeta } from './common';
import type {
  ActiveWorktree,
  Daemon,
  DaemonTrigger,
  DaemonTriggerInjectedConvention,
  DaemonTriggerLog,
  DiscoveredWorktreeListItem,
  OsType,
  ResolveDaemonTriggerSettingsInput,
  RunnerType,
  WorktreeListItem,
} from '@agentteams/runner-request/ui/lib/api';
export type {
  ActiveWorktree,
  Daemon,
  DaemonTokenUsage,
  DaemonTrigger,
  DaemonTriggerChainKind,
  DaemonTriggerConventionSource,
  DaemonTriggerInjectedConvention,
  DaemonTriggerLog,
  DaemonTriggerLogCategory,
  DiscoveredWorktreeListItem,
  OsType,
  QueueRunnerState,
  ResolveDaemonTriggerSettingsInput,
  RunnerType,
  RunnerUpdateFailureReason,
  WorktreeListItem,
} from '@agentteams/runner-request/ui/lib/api';
export {
  getDiscoveredWorktreeDisplayName,
  osTypeValues,
  runnerTypeValues,
} from '@agentteams/runner-request/ui/lib/api';

// 러너 타입별 Effort(추론 강도) 전체 허용 집합의 SSOT는 @agentteams/core-constants이다.
// 시스템/개인 모델 관리 화면의 체크박스 옵션은 이 함수로 파생한다. 실제 요청 가능 레벨은
// 각 모델의 supportedEffortLevels(이 집합의 부분집합)로 다시 제한된다.
export const getRunnerEffortLevels = (runnerType: RunnerType): readonly string[] =>
  RUNNER_EFFORT_LEVELS[runnerType as keyof typeof RUNNER_EFFORT_LEVELS] ?? [];

// 특정 러너 타입이 Effort 선택을 지원하는지 여부. 지원 러너 목록의 SSOT는 RUNNER_EFFORT_LEVELS(@agentteams/core-constants)이다.
export const runnerTypeSupportsEffort = (runnerType: RunnerType): boolean => isEffortRunnerType(runnerType);

// 러너 트리거 상태값의 단일 진실 소스(SSOT). 백엔드 DaemonTriggerStatus
// (api/src/constants/enums.ts) 8개 상태 + 목록 쿼리가 허용하는 가상 'active'
// (api/src/schemas/daemon.ts list enum [...daemonStatusValues, 'active'])까지 포함한다.
// 라벨/배지는 web/src/lib/badgeColors.ts 의 daemonTriggerStatusLabel·daemonTriggerStatusBadge
// Record 키와 이 배열이 정합되므로, 신규 상태 추가 시 백엔드 enum → badgeColors Record →
// 이 배열 순으로 반영하면 필터 옵션이 자동 파생된다.
export const daemonTriggerStatusValues = [
  'PENDING',
  'IN_PROGRESS',
  'CANCEL_REQUESTED',
  'DONE',
  'CANCELLED',
  'FAILED',
  'REJECTED',
  'NEEDS_REVIEW',
] as const;

// 가상 상태: 백엔드 list 쿼리는 'active'를 받아 PENDING/IN_PROGRESS/CANCEL_REQUESTED를
// 합산 조회한다. 상태값 리터럴이 아닌 필터 전용 값.
export const daemonTriggerActiveVirtualStatus = 'active' as const;
export type DaemonTriggerFilterStatus =
  | (typeof daemonTriggerStatusValues)[number]
  | typeof daemonTriggerActiveVirtualStatus;

export type DaemonListResult = {
  daemons: Daemon[];
  onlineThresholdMs: number;
};

export type SharedRunnerAgentConfig = {
  id: string;
  name: string;
  osType: OsType | null;
  targetDaemonId: string | null;
  shared?: boolean;
  planModeOnly?: boolean;
  useWorktree?: boolean;
  createdByName: string | null;
  createdAt: string;
  runnerCheckout?: { id: string; daemonId: string } | null;
};

export type SharedDaemon = Daemon & {
  ownerMemberId: string;
  ownerNickname: string;
  shareId: string;
  sharedProjectId: string;
  planModeOnly: boolean;
  /// 공유 단위 허용 엔진 목록. 빈 배열이면 러너의 supportedEngines를 그대로 따릅니다.
  allowedEngines: RunnerType[];
  agentConfigs: SharedRunnerAgentConfig[];
};

export type DaemonShare = {
  id: string;
  daemonId: string;
  ownerMemberId: string;
  projectId: string;
  projectName: string | null;
  /// 계정 범위 화면에서 `/agent-configs?teamId=...&projectId=...` 딥링크를 만들기 위한 팀 ID.
  teamId: string | null;
  teamName: string | null;
  createdByMemberId: string;
  planModeOnly: boolean;
  /// 공유 단위 허용 엔진 목록. 빈 배열이면 러너의 supportedEngines를 그대로 따릅니다.
  allowedEngines: RunnerType[];
  /// 이 공유에서 수신자 요청 대상에 노출되는 소유자 AgentConfig ID 목록. 빈 배열이면 노출 없음.
  exposedAgentConfigIds: string[];
  status: 'ACTIVE' | 'REVOKED';
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreatedDaemon = Daemon & {
  token: string;
};

export type AttachmentDraftInput = {
  key: string;
  originalName: string;
};

export type CreateDaemonTriggerInput = {
  prompt: object;
  agentConfigId: string;
  /// 공유 러너 사용 시 러너 소유자 AgentConfig가 위치한 프로젝트와 다를 수 있어 명시적으로 지정합니다.
  projectId?: string;
  targetDaemonId?: string;
  runnerType?: string;
  model?: string;
  fastMode?: boolean;
  /// 요청 Effort 레벨. null/미전달은 모델 기본값을 의미합니다.
  effort?: string | null;
  planMode?: boolean;
  useWorktree?: boolean;
  repositoryId?: string;
  baseBranch?: string;
  worktreeId?: string;
  /// 발견(discovered) worktree를 RunnerBox로 재사용할 때 선택한 레지스트리 ID. 소유 daemon에 고정됩니다.
  discoveredWorktreeId?: string;
  userConventionIds?: string[];
  dispatchPolicy?: {
    policyId: string;
    entityType: 'PLAN' | 'CO_ACTION' | 'COMPLETION_REPORT' | 'POST_MORTEM' | 'CODE_REVIEW';
    entityId: string;
    additionalPrompt: object;
  };
  attachmentDrafts?: AttachmentDraftInput[];
};

export type ContinueDaemonTriggerInput = {
  prompt: object;
  runnerType?: string;
  model?: string | null;
  fastMode?: boolean;
  effort?: string | null;
  planMode?: boolean;
  userConventionIds?: string[];
  attachmentDrafts?: AttachmentDraftInput[];
};

export type DaemonTriggerPromptPreview = {
  runnerPrompt: string;
  planType: string | null;
};

export type CreateDaemonInput = {
  label?: string;
  supportedEngines?: RunnerType[];
  sortOrder?: number;
};

export type UpdateDaemonInput = {
  label?: string;
  supportedEngines?: RunnerType[];
  sortOrder?: number;
};

export type CreateDaemonShareInput = {
  projectId: string;
  planModeOnly?: boolean;
  allowedEngines?: RunnerType[];
  /// 후보 집합 전체에 대한 full replacement. 빈 배열은 "모두 비노출"로 유효한 값입니다.
  exposedAgentConfigIds?: string[];
};

export type UpdateDaemonShareInput = {
  planModeOnly?: boolean;
  allowedEngines?: RunnerType[];
  /// 후보 집합 전체에 대한 full replacement. 빈 배열은 "모두 비노출"로 유효한 값입니다.
  exposedAgentConfigIds?: string[];
};

/// 공유 시 노출 대상으로 고를 수 있는 소유자 AgentConfig 후보(안전한 최소 projection).
export type DaemonShareCandidate = {
  id: string;
  name: string;
  osType: OsType | null;
  targetDaemonId: string | null;
  shared: boolean;
  planModeOnly: boolean;
  useWorktree: boolean;
  createdAt: string;
};

type DaemonEnvelope = {
  data: Daemon;
};

type DaemonListEnvelope = {
  data: Daemon[];
  meta: {
    onlineThresholdMs: number;
  };
};

type DaemonShareEnvelope = {
  data: DaemonShare;
};

type DaemonShareListEnvelope = {
  data: DaemonShare[];
};

type SharedDaemonListEnvelope = {
  data: SharedDaemon[];
};

type CreateDaemonEnvelope = {
  data: CreatedDaemon;
};

/** 서버가 활성 트리거 충돌을 WARN으로 판정했을 때만 채워진다. 생성은 그대로 진행된다. */
export type DaemonTriggerActiveWarning = {
  reason: 'MAIN_CHECKOUT_BUSY' | 'SAME_WORKTREE_BUSY' | 'QUEUED_BEHIND_PENDING';
};

type DaemonTriggerEnvelope = {
  data: DaemonTrigger;
  warning?: DaemonTriggerActiveWarning | null;
};

type DaemonTriggerResolvedSettingsEnvelope = {
  data: DaemonTriggerResolvedSettings;
};

type DaemonTriggerPagedListEnvelope = {
  data: DaemonTrigger[];
  meta: PaginationMeta;
};

type DaemonTriggerLogsEnvelope = {
  data: {
    logs: DaemonTriggerLog[];
    nextCursor: string | null;
  };
};

type ActiveWorktreeListEnvelope = {
  data: ActiveWorktree[];
};

export const fetchDaemons = async (token: string): Promise<DaemonListResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch daemons: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonListEnvelope;
  return {
    daemons: payload.data,
    onlineThresholdMs: payload.meta.onlineThresholdMs,
  };
};

export const fetchDaemonShares = async (token: string, daemonId?: string): Promise<DaemonShare[]> => {
  const params = new URLSearchParams();
  if (daemonId) {
    params.set('daemonId', daemonId);
  }

  const query = params.toString();
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons/shares${query ? `?${query}` : ''}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch daemon shares: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonShareListEnvelope;
  return payload.data;
};

export const fetchDaemonShareCandidates = async (
  token: string,
  daemonId: string,
  projectId: string,
): Promise<DaemonShareCandidate[]> => {
  const params = new URLSearchParams({ projectId });
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/daemons/${daemonId}/share-candidates?${params.toString()}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch daemon share candidates: ${response.status}`);
  }

  const payload = (await response.json()) as { data: DaemonShareCandidate[] };
  return payload.data;
};

export const fetchSharedDaemons = async (token: string, projectId: string): Promise<SharedDaemon[]> => {
  const params = new URLSearchParams({ projectId });
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons/shared?${params.toString()}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch shared daemons: ${response.status}`);
  }

  const payload = (await response.json()) as SharedDaemonListEnvelope;
  return payload.data;
};

export const createDaemonShare = async (
  token: string,
  daemonId: string,
  input: CreateDaemonShareInput,
): Promise<DaemonShare> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons/${daemonId}/shares`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create daemon share: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonShareEnvelope;
  return payload.data;
};

export const updateDaemonShare = async (
  token: string,
  daemonId: string,
  shareId: string,
  input: UpdateDaemonShareInput,
): Promise<DaemonShare> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons/${daemonId}/shares/${shareId}`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update daemon share: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonShareEnvelope;
  return payload.data;
};

export const revokeDaemonShare = async (token: string, daemonId: string, shareId: string): Promise<DaemonShare> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons/${daemonId}/shares/${shareId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to revoke daemon share: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonShareEnvelope;
  return payload.data;
};

export const createDaemon = async (token: string, input?: CreateDaemonInput): Promise<CreatedDaemon> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input ?? {}),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create daemon: ${response.status}`);
  }

  const payload = (await response.json()) as CreateDaemonEnvelope;
  return payload.data;
};

export const deleteDaemon = async (token: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete daemon: ${response.status}`);
  }
};

export const updateDaemon = async (token: string, id: string, input: UpdateDaemonInput): Promise<Daemon> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update daemon: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonEnvelope;
  return payload.data;
};

export const requestDaemonRestart = async (token: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemons/${id}/restart-request`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to request daemon restart: ${response.status}`);
  }
};

export type CreateDaemonTriggerResult = {
  trigger: DaemonTrigger;
  warning: DaemonTriggerActiveWarning | null;
};

/**
 * 생성 응답의 `warning`까지 그대로 돌려준다.
 *
 * `createDaemonTrigger`는 `data`만 반환하도록 여러 화면이 이미 기대하고 있어 시그니처를 바꾸지
 * 않는다. 경고가 필요한 호출자만 이쪽을 쓴다.
 */
export const createDaemonTriggerWithWarning = async (
  token: string,
  input: CreateDaemonTriggerInput,
): Promise<CreateDaemonTriggerResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create daemon trigger: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerEnvelope;
  return { trigger: payload.data, warning: payload.warning ?? null };
};

export const createDaemonTrigger = async (token: string, input: CreateDaemonTriggerInput): Promise<DaemonTrigger> =>
  (await createDaemonTriggerWithWarning(token, input)).trigger;

export const resolveDaemonTriggerSettings = async (
  token: string,
  input: ResolveDaemonTriggerSettingsInput,
): Promise<DaemonTriggerResolvedSettings> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/resolve-settings`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to resolve daemon trigger settings: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerResolvedSettingsEnvelope;
  return payload.data;
};

// 시스템 관리자 전용(디버깅): 영속된 트리거에 전송된 러너 프롬프트를 재구성해 받아온다.
export const fetchDaemonTriggerPromptPreview = async (
  token: string,
  triggerId: string,
): Promise<DaemonTriggerPromptPreview> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${triggerId}/prompt-preview`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch daemon trigger prompt preview: ${response.status}`);
  }

  const payload = (await response.json()) as { data: DaemonTriggerPromptPreview };
  return payload.data;
};

export const fetchDaemonTriggerById = async (token: string, id: string): Promise<DaemonTrigger> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch daemon trigger: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerEnvelope;
  return payload.data;
};

export const deleteDaemonTrigger = async (token: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete daemon trigger: ${response.status}`);
  }
};

export const reviewDaemonTrigger = async (
  token: string,
  triggerId: string,
  decision: 'approve' | 'reject',
): Promise<DaemonTrigger> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${triggerId}/review`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify({ decision }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to review daemon trigger: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerEnvelope;
  return payload.data;
};

export const continueDaemonTrigger = async (
  token: string,
  triggerId: string,
  input: ContinueDaemonTriggerInput,
): Promise<DaemonTrigger> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${triggerId}/continue`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to continue daemon trigger: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerEnvelope;
  return payload.data;
};

export const cancelDaemonTrigger = async (token: string, triggerId: string): Promise<DaemonTrigger> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${triggerId}/cancel`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to cancel daemon trigger: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerEnvelope;
  return payload.data;
};

export const fetchDaemonTriggers = async (
  token: string,
  params?: {
    status?: DaemonTrigger['status'];
    projectId?: string;
    agentConfigId?: string;
    agentConfigIds?: string[];
    targetDaemonId?: string;
    runnerType?: string;
    prompt?: string;
    planMode?: boolean;
    useWorktree?: boolean;
    isFavorite?: boolean;
    groupByChain?: boolean;
    planId?: string;
    limit?: number;
    page?: number;
    pageSize?: number;
    dateFrom?: string;
    dateTo?: string;
  },
): Promise<{ data: DaemonTrigger[]; meta: PaginationMeta }> => {
  const query = new URLSearchParams();
  if (params?.status) {
    query.set('status', params.status);
  }
  if (params?.projectId) {
    query.set('projectId', params.projectId);
  }
  if (params?.agentConfigId) {
    query.set('agentConfigId', params.agentConfigId);
  }
  if (params?.targetDaemonId) {
    query.set('targetDaemonId', params.targetDaemonId);
  }
  if (params?.runnerType) {
    query.set('runnerType', params.runnerType);
  }
  if (params?.prompt) {
    query.set('prompt', params.prompt);
  }
  if (params?.agentConfigIds && params.agentConfigIds.length > 0) {
    query.set('agentConfigIds', params.agentConfigIds.join(','));
  }
  if (typeof params?.planMode === 'boolean') {
    query.set('planMode', String(params.planMode));
  }
  if (typeof params?.useWorktree === 'boolean') {
    query.set('useWorktree', String(params.useWorktree));
  }
  if (params?.isFavorite === true) {
    query.set('isFavorite', 'true');
  }
  if (params?.groupByChain) {
    query.set('groupByChain', 'true');
  }
  if (params?.planId) {
    query.set('planId', params.planId);
  }
  if (typeof params?.limit === 'number') {
    query.set('limit', String(params.limit));
  }
  if (typeof params?.page === 'number') {
    query.set('page', String(params.page));
  }
  if (typeof params?.pageSize === 'number') {
    query.set('pageSize', String(params.pageSize));
  }
  if (params?.dateFrom) {
    query.set('dateFrom', params.dateFrom);
  }
  if (params?.dateTo) {
    query.set('dateTo', params.dateTo);
  }

  const queryString = query.toString();
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/daemon-triggers${queryString ? `?${queryString}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch daemon triggers: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerPagedListEnvelope;
  return payload;
};

export const fetchDaemonTriggerLogs = async (
  token: string,
  triggerId: string,
  params?: { cursor?: string; limit?: number },
): Promise<{ logs: DaemonTriggerLog[]; nextCursor: string | null }> => {
  const query = new URLSearchParams();
  if (params?.cursor) {
    query.set('cursor', params.cursor);
  }
  if (typeof params?.limit === 'number') {
    query.set('limit', String(params.limit));
  }

  const queryString = query.toString();
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/daemon-triggers/${triggerId}/logs${queryString ? `?${queryString}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch daemon trigger logs: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerLogsEnvelope;
  return payload.data;
};

type DaemonTriggerConventionListEnvelope = {
  data: DaemonTriggerInjectedConvention[];
};

export const fetchDaemonTriggerInjectedConventions = async (
  token: string,
  triggerId: string,
): Promise<DaemonTriggerInjectedConvention[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${triggerId}/conventions`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch injected conventions: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerConventionListEnvelope;
  return payload.data;
};

export const fetchActiveWorktreesByAgentConfig = async (
  token: string,
  agentConfigId: string,
): Promise<ActiveWorktree[]> => {
  const query = new URLSearchParams({ agentConfigId });
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/worktree/active?${query.toString()}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch active worktrees: ${response.status}`);
  }

  const payload = (await response.json()) as ActiveWorktreeListEnvelope;
  return payload.data;
};

export const requestWorktreeRemoval = async (token: string, triggerId: string): Promise<DaemonTrigger> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${triggerId}/worktree/remove`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to request worktree removal: ${response.status}`);
  }

  const payload = (await response.json()) as DaemonTriggerEnvelope;
  return payload.data;
};

type WorktreeListEnvelope = {
  data: WorktreeListItem[];
};

type WorktreeAliasEnvelope = {
  data: { worktreeId: string; alias: string | null };
};

export const fetchWorktreeList = async (
  token: string,
  projectId: string,
  includeRemoved: boolean = false,
): Promise<WorktreeListItem[]> => {
  const query = new URLSearchParams({ projectId, includeRemoved: String(includeRemoved) });
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/worktrees?${query.toString()}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch worktree list: ${response.status}`);
  }

  const payload = (await response.json()) as WorktreeListEnvelope;
  return payload.data;
};

type DiscoveredWorktreeListEnvelope = {
  data: DiscoveredWorktreeListItem[];
};

type DiscoveredWorktreeLabelEnvelope = {
  data: { id: string; userLabel: string | null };
};

export const fetchDiscoveredWorktrees = async (
  token: string,
  projectId: string,
): Promise<DiscoveredWorktreeListItem[]> => {
  const query = new URLSearchParams({ projectId });
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/daemon-triggers/discovered-worktrees?${query.toString()}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch discovered worktrees: ${response.status}`);
  }

  const payload = (await response.json()) as DiscoveredWorktreeListEnvelope;
  return payload.data;
};

// MISSING 발견 worktree의 레지스트리 데이터만 삭제한다. 로컬 worktree/디렉터리/브랜치는 삭제되지 않는다.
export const deleteDiscoveredWorktree = async (token: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/discovered-worktrees/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete discovered worktree: ${response.status}`);
  }
};

export const updateDiscoveredWorktreeLabel = async (
  token: string,
  id: string,
  label: string,
): Promise<{ id: string; userLabel: string | null }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/discovered-worktrees/${id}/label`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify({ label }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update discovered worktree label: ${response.status}`);
  }

  const payload = (await response.json()) as DiscoveredWorktreeLabelEnvelope;
  return payload.data;
};

export const updateWorktreeAlias = async (
  token: string,
  worktreeId: string,
  projectId: string,
  alias: string,
): Promise<{ worktreeId: string; alias: string | null }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/worktrees/${worktreeId}/alias`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify({ projectId, alias }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update worktree alias: ${response.status}`);
  }

  const payload = (await response.json()) as WorktreeAliasEnvelope;
  return payload.data;
};

export const setDaemonTriggerFavorite = async (
  token: string,
  triggerId: string,
  isFavorite: boolean,
): Promise<{ isFavorite: boolean }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/daemon-triggers/${triggerId}/favorite`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ isFavorite }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to set favorite: ${response.status}`);
  }

  const payload = (await response.json()) as { data: { isFavorite: boolean } };
  return payload.data;
};
